import '#/index.css';
import type { ReactNode } from 'react';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Provider as NotificationProvider, Viewport as NotificationViewport } from '@radix-ui/react-toast';
import { TooltipProvider } from '@radix-ui/react-tooltip';
import { datadogRum } from '@datadog/browser-rum';
import { ErrorBoundary, reactPlugin } from '@datadog/browser-rum-react';
import { flex } from '../styled-system/patterns';
import { AppRouter } from '#/AppRouter';
import { SnackbarProvider } from '#/utils/snackbar/snackbarProvider';
import { MOCK_MODE } from '#/utils/env';
import { DATADOG_RUM_APPLICATION_ID, DATADOG_RUM_CLIENT_TOKEN } from '#/config';

datadogRum.init({
  applicationId: DATADOG_RUM_APPLICATION_ID,
  clientToken: DATADOG_RUM_CLIENT_TOKEN,
  // `site` refers to the Datadog site parameter of your organization
  // see https://docs.datadoghq.com/getting_started/site/
  site: 'ap1.datadoghq.com',
  service: 'zenid-mypage-web',
  env: import.meta.env.VITE_APP_ENV,
  version: import.meta.env.VITE_RELEASE_VERSION,
  sessionSampleRate: 100,
  sessionReplaySampleRate: 20,
  trackUserInteractions: true,
  trackResources: true,
  trackLongTasks: true,
  defaultPrivacyLevel: 'mask-user-input',
  betaPlugins: [reactPlugin({ router: true })],
  // RUMとAPMを連携するための設定
  // バックエンドAPIへのリクエストにトレースヘッダーを注入してRUMとAPMのトレースを関連付ける
  // https://docs.datadoghq.com/ja/real_user_monitoring/platform/connect_rum_and_traces/
  allowedTracingUrls: [
    {
      match: import.meta.env.VITE_API_URL,
      propagatorTypes: ['datadog', 'tracecontext'],
    },
  ],
});

function ErrorFallback({ resetError, error }: { resetError: () => void; error: unknown }) {
  return (
    <p>
      Oops, something went wrong! <strong>{String(error)}</strong> <button onClick={resetError}>Retry</button>
    </p>
  );
}

async function deferRender() {
  // Mockモードで起動しているときのみMockServerを起動させる
  if (MOCK_MODE) {
    const { worker } = await import('./api/mocks/setupMockServer');
    return worker.start();
  }
  return Promise.resolve();
}

export function NotificationProviderWrapper({ children }: { children: ReactNode }) {
  return (
    <NotificationProvider>
      <SnackbarProvider>{children}</SnackbarProvider>
      <NotificationViewport
        className={flex({
          // widthとpaddingが機能していない
          // width: '100vw',
          // padding: '{spacing.20}',
          flexDirection: 'column',
          position: 'fixed',
          bottom: '0',
          zIndex: 99999,
          gap: '{spacing.12}',
          alignItems: 'center',
        })}
        style={{
          // flex()内のwidthとpaddingが機能していないのでstyle属性に渡す
          // TODO: 原因について別途調査を行う
          width: '100vw',
          padding: '0 20px 20px 20px',
        }}
      />
    </NotificationProvider>
  );
}

deferRender().then(() => {
  const queryClient = new QueryClient();
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <ErrorBoundary fallback={ErrorFallback}>
        <QueryClientProvider client={queryClient}>
          <NotificationProviderWrapper>
            <TooltipProvider>
              <AppRouter />
            </TooltipProvider>
          </NotificationProviderWrapper>
        </QueryClientProvider>
      </ErrorBoundary>
    </StrictMode>
  );
});
